/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import { ofType } from 'redux-observable';
import { of, empty, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../actions/manager';
import constants, { publicConstants } from '../../constants';
import { getResolvedTargetName } from '../../router/routing-manager';
import * as actionType from '../../store/state/actions';
import * as StateManager from '../../store/state/state-manager';
import {
  addActionsToParentContext,
  addPreviewResetAction,
  getActionPayload,
  getActivationDetails,
  getContext,
  getDirtyDetails,
  processDataToDispatchErrorAction,
  processViewResponse,
  SCOPE_TYPES
} from '../../store/state/epics/util';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import {
  getContainerName,
  isParentAndChildContextHoldingSameItem,
  updateKeyAndFlowName,
  promiseHandler,
  isCoexistenceCase,
  canLaunchCaseInModal
} from '../common-helper';
import { getActiveContainerItemName } from '../../container/container-utils';
import Utils from '../../utils/utils';
import BroadcastUtils from '../../utils/broadcast-utils';
import { isAssignmentInCreateStage } from '../../utils/case-utils';
import { getParentContextName } from '../../utils/context-utils';
import { publishAssignmentOpenedEvent } from '../common-events';
import type { Action } from '../../store/state/state-manager';
import EnvironmentInfo from '../../environment-info';
import type { Actionout } from '../../types';
import NavigationUtils from '../../utils/navigation-utils';
import HeaderProcessor from '../../service_broker/header-processor';
import CaseUtils from '../utils';

import { executeOpenAssignmentRequest } from './async-request';
import {
  addActionToActivateContainerItem,
  getContainerNameForCaseStage,
  getViewType,
  setDirectUrlUpdation,
  processAppropriateContainerItem,
  setIsFirstAssignmentForCoexistence,
  validateAndSetRemoteCaseParams
} from './helper';
import { OPEN_ASSIGNMENT } from './action-creator';

const { RESOURCE_TYPES } = publicConstants;

export const openAssignment = (action$: Observable<Action>) => {
  const isBrowser = EnvironmentInfo.getRuntime() === 'Browser';
  let actionMgrID = 0;
  let actionsOut: Actionout[] = [];
  const errorDetails: object[] = [];
  let isGetNextWork = false;
  let isTraditionalUICase = false;
  return action$.pipe(
    ofType(OPEN_ASSIGNMENT),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return {
        ...action.payload,

        ...(isBrowser ? { insKey: action.payload.assignmentID } : { assignmentID: action.payload.assignmentID })
      };
    }),
    mergeMap((actionInPayload: Action['payload']) => {
      isGetNextWork = actionInPayload.isGetNextWork;
      const { context } = actionInPayload;
      let viewType;
      if ((actionInPayload?.isChild || actionInPayload?.isRemote) && actionInPayload?.isMultiDoc) {
        viewType = SCOPE_TYPES.FORM;
      } else {
        viewType = getViewType(actionInPayload);
      }
      actionInPayload.viewType = viewType;
      const containerName = getContainerName(actionType.ACTION_OPENASSIGNMENT, actionInPayload);
      const target = getResolvedTargetName(containerName, context);

      // checking for active container item with given actionInPayload
      const { activate, activateContainerActionPayload } = getActivationDetails(
        RESOURCE_TYPES.ASSIGNMENT,
        target,
        actionInPayload
      );

      // return activate container item when an item is found in container with given actionInPayload
      // and assignmentID is matching with current active one in that item
      if (activate && !actionInPayload.isParallelCall) {
        return of({
          type: actionType.ACTIVATE_CONTAINER_ITEM,
          activateContainerActionPayload,
          actionInPayload
        });
      }

      /*
        check if current assignment is parallelAssignment to the assignment which is currently active
          1. if it is parallel, skip the dirty check,
          2. else check for dirty, to proceed further.
      */
      const activeContainerItemName = getActiveContainerItemName(target);
      let executionConfig;

      if (activateContainerActionPayload?.isParallelAssignment) {
        executionConfig = { continueExecution: true, removeActionPayload: {} };
      } else if (activeContainerItemName) {
        executionConfig = getDirtyDetails(target);
      } else {
        executionConfig = { continueExecution: true, removeActionPayload: {} };
      }

      // Destructuring the values from executionConfig for further use
      const { continueExecution, removeActionPayload } = executionConfig;

      // if user presses cancel on dirty confirm box, return empty observable to stop the chain process.
      if (!continueExecution) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
        return empty();
      }

      isTraditionalUICase = isCoexistenceCase(actionInPayload.className);
      actionInPayload.isTraditionalUICase = isTraditionalUICase;
      if (EnvironmentInfo.isWSSPortal()) {
        actionInPayload.headers = { ...actionInPayload.headers, [constants.X_ORIGIN_CHANNEL]: 'WSS' };
      }
      // dxapi call
      return executeOpenAssignmentRequest(actionInPayload, {
        removeActionPayload
      });
    }),
    // eslint-disable-next-line sonarjs/cognitive-complexity
    mergeMap(async (chainedData) => {
      if (chainedData?.type === actionType.ERROR) {
        if (isGetNextWork) {
          const { payload } = chainedData;
          const { err } = payload;
          const errorStatus = err.response.status;
          errorDetails.push({ status: Number(errorStatus) });
        }
        actionsOut.push(processDataToDispatchErrorAction(chainedData));
        return actionsOut;
      }

      const {
        type,
        actionInPayload,
        response,
        viewType,
        headers,
        removeActionPayload,
        activateContainerActionPayload
      } = chainedData;
      if (response?.redirectInfo?.jsAction === 'openStage') {
        const stageID = response?.redirectInfo?.stageID;

        const theType = OPEN_ASSIGNMENT;
        const caseID = response?.data?.caseInfo?.ID;
        actionInPayload.isOpenStage = true;
        actionInPayload.stageID = stageID;
        actionInPayload.jsActionQueryParams = response?.redirectInfo?.jsActionQueryParams;
        const openAssignmentAction = {
          type: theType,
          payload: { ...actionInPayload, caseID, headers }
        };
        actionsOut.push(openAssignmentAction);
        return actionsOut;
      }
      const { context: actionInContext, caseViewMode: actionInCaseViewMode, debugInfo } = actionInPayload;
      const caseTypeID = response?.data?.caseInfo?.caseTypeID;
      isTraditionalUICase = isCoexistenceCase(caseTypeID);
      actionInPayload.isTraditionalUICase = isTraditionalUICase;

      let containerName = getContainerNameForCaseStage(response, actionInPayload);

      if (isTraditionalUICase) {
        containerName = actionInPayload.containerName ?? containerName;
      }
      const target = getResolvedTargetName(containerName, actionInContext);

      // activate the container item which already present in containers data (redux)
      if (type === actionType.ACTIVATE_CONTAINER_ITEM) {
        await addActionToActivateContainerItem(actionsOut, activateContainerActionPayload, { actionInPayload, target });

        addPreviewResetAction(actionsOut);
        return actionsOut;
      }

      const content = response?.data?.caseInfo?.content;
      const ID = response?.data?.caseInfo?.ID;
      const uiConfigName = response?.uiResources?.root?.config?.name;
      const uiConfigContext = response?.uiResources?.root?.config?.context;

      if (content && !content.pyViewName && uiConfigName && actionInPayload.viewType !== 'page') {
        Utils.setIn(content, 'pyViewName', uiConfigName);
        Utils.setIn(content, 'pyViewContext', uiConfigContext);
      }

      const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
      // semantic url updation
      setDirectUrlUpdation(translatedDXAPIResponse, actionInPayload);
      const context = getContext(target, containerName, actionInContext);

      const debugInformation = actionInPayload.debugInfo || {};
      if (!debugInformation?.debugId) {
        // get it from context
        debugInformation.debugId =
          HeaderProcessor.getCoreHeadersFromContext(actionInContext)?.debugInfo?.[constants.COREHEADER_APP_DEBUG_ID];
      }
      if (debugInformation && debugInformation.debugId) {
        NavigationUtils.setComponentCache(ID, HeaderProcessor.addDebugHeaders(debugInformation));
      }

      /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
      const options = { acName: target, target, context };

      await processViewResponse(translatedDXAPIResponse, actionInPayload, options);

      const commonInfo = {
        target,
        context,
        containerItemID: context,
        headers,
        parentContext: getParentContextName(actionInPayload?.context),
        /* Flag to show minimize button in create stage modal dialog */
        isMinimizable: isAssignmentInCreateStage(Utils.getIn(translatedDXAPIResponse, 'data.caseInfo')),
        doDeleteCaseSummary: actionInPayload.doDeleteCaseSummary,
        acTertiary: actionInPayload.acTertiary,
        resourceType: publicConstants.ASSIGNMENT,
        debugInfo
      };

      // updating key and flowName
      updateKeyAndFlowName(commonInfo, translatedDXAPIResponse, {
        skipFlowNameUpdate: actionInPayload?.caseViewMode === constants.REVIEW_MODE
      });

      const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
      if (actionInPayload.isProcessAction) {
        payload.data.isProcessAction = actionInPayload.isProcessAction;
      }
      if (isTraditionalUICase) {
        payload.data.caseInfo.content.coexistenceAction = 'openAssignment';
      }
      CaseUtils.setTraditionalGadgetId(payload.data);
      setIsFirstAssignmentForCoexistence(actionInPayload, payload);
      processAppropriateContainerItem(
        payload,
        target,
        actionsOut,
        actionInPayload,
        response,
        actionInCaseViewMode,
        removeActionPayload
      );
      validateAndSetRemoteCaseParams(caseTypeID, payload.data.caseInfo);

      if (viewType === SCOPE_TYPES.FORM && isParentAndChildContextHoldingSameItem(payload.containerItemID)) {
        payload.skipMergeAssignmentsAndActions = false;
        addActionsToParentContext(payload, actionsOut, uiConfigContext);
      }
      addPreviewResetAction(actionsOut);
      publishAssignmentOpenedEvent({
        actionInContext,
        assignmentID: actionInPayload.assignmentID,
        caseKey: payload.key
      });
      return actionsOut;
    }),
    map(() => {
      const actionManagerID = actionMgrID;
      const launchCaseInModal = canLaunchCaseInModal();
      const isCaseInCreateStage = isAssignmentInCreateStage(Utils.getIn(actionsOut[0]?.payload, 'data.caseInfo'));
      if (isTraditionalUICase && launchCaseInModal && isCaseInCreateStage) {
        BroadcastUtils.postCreateCaseMessage(
          actionsOut[0]?.payload?.data?.caseInfo?.assignments?.[0]?.ID,
          StateManager.dispatch,
          actionsOut
        );
      } else {
        StateManager.dispatch(actionsOut);
      }
      promiseHandler(actionsOut, actionManagerID, errorDetails);
      if (actionsOut[0]?.payload?.isParallelAssignment) {
        StateManager.dispatch({
          type: OPEN_ASSIGNMENT,
          payload: { ...actionsOut[0].payload, isParallelCall: true }
        });
      }
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default openAssignment;
